import {
  ResponseResult,
  ResponseStatus,
  type QuestionResponseDetails,
  type SubResponse,
  type SubQuestionResponseEntry,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
} from './preserveResponseFields';

/**
 * Arguments for {@link buildSubQuestionAnswerUpdate}. Bundled in a
 * single object so future fields (sub-question naReasonId, sub
 * attachments, `expectedLastUpdatedAt`) slot in without changing
 * the caller signature.
 */
export interface BuildSubQuestionAnswerUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — recorded as `respondedBy` / `lastUpdatedBy`. */
  readonly alias: string;
  /** The sub-question the auditor just tapped. */
  readonly subQuestionId: string;
  /** Pass / Fail / N/A from the tap. */
  readonly result: ResponseResult;
  /**
   * The parent question's cached response, or undefined for a
   * never-answered parent. Preservation is the same as
   * `buildAnswerUpdate` — comment / naReason / mediaIds / taskIds
   * survive the round-trip via {@link buildPreservedResponseData}.
   */
  readonly previous?: QuestionResponseDetails | undefined;
  /**
   * Existing sub-question responses on the parent. Every entry
   * except the target `subQuestionId` is preserved as-is (result,
   * comment, naReason, mediaIds, taskIds); the target is
   * overwritten with the tapped result while its other fields
   * remain untouched. If no entry matches, a new one is appended.
   *
   * Undefined / empty is fine — the target becomes the only entry.
   */
  readonly subQuestionResponses?: readonly SubQuestionResponseEntry[] | undefined;
  /**
   * Total number of sub-questions on the parent (from the question
   * definition, not the response array — a never-answered sub has
   * no cache row). Drives the `responseStatus` derivation: complete
   * coverage with every child answered is `COMPLETED`, except an
   * all-NA set uses `NA`.
   */
  readonly totalSubQuestions: number;
}

/**
 * Builds the mutation input for a Pass / Fail / N/A save on a
 * SUB-QUESTION. Mirrors {@link buildAnswerUpdate} in structure but
 * differs in where the tapped result lands on the wire:
 *
 *   - Top-level `responseResult` is INTENTIONALLY UNSET. The server
 *     (and, on load, the client's `getMainQuestionStatus`) derives
 *     the parent's aggregate result from the children. Sending a
 *     top-level result on a sub-answer save would race the
 *     server-side aggregation.
 *   - Top-level `responseStatus` is derived from the merged
 *     children: `PENDING` while any child is missing or unanswered,
 *     `NA` when every child is NA, and `COMPLETED` for every other
 *     complete combination. Server re-derives on receive; we send
 *     the structurally complete status without setting the parent's
 *     aggregate result.
 *   - `subResponses` carries every child. The target sub-answer is
 *     replaced (result overwritten, everything else — comment,
 *     naReason, media, tasks — preserved from the read side) or
 *     inserted if never answered. Non-target children pass through
 *     {@link projectSubResponsesForPreservation} unchanged: full
 *     preservation of result AND comment / naReason / media / tasks,
 *     so a sub-answer save on sub-A does not silently clear
 *     server-side comments on sub-B (CR-V6 latent-drop fix).
 *
 * Pure — no react, no side effects. Consumed by the sub-question
 * draft hook and by the sub-question cache patch (so both send the
 * same shape to the wire and the optimistic cache).
 */
export function buildSubQuestionAnswerUpdate(
  args: BuildSubQuestionAnswerUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const mergedSubResponses = mergeSubResponses(
    args.subQuestionResponses,
    args.subQuestionId,
    args.result,
  );

  const responseStatus = deriveTopLevelStatus(mergedSubResponses, args.totalSubQuestions);

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus,
    // Deliberately not setting top-level responseResult — see docblock.
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
    subResponses: mergedSubResponses,
  };

  const preservedResponse = buildPreservedResponseData(args.previous);
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  return input;
}

/**
 * Merges the target sub-answer into the projected sub-response
 * array. The projection preserves every child's result / comment /
 * naReason / mediaIds / taskIds (via
 * {@link projectSubResponsesForPreservation}); the merge step only
 * touches the TARGET entry — replacing its `result` in place while
 * keeping its comment / naReason / media / tasks intact, or
 * appending a new `{ subQuestionId, result }` when the target
 * doesn't already have an entry.
 */
function mergeSubResponses(
  existing: readonly SubQuestionResponseEntry[] | undefined,
  targetSubQuestionId: string,
  targetResult: ResponseResult,
): SubResponse[] {
  // Clear the target sub's naReason on the wire when its new
  // result isn't NA. Same rationale as the parent path in
  // buildAnswerUpdate — a previously-picked reason must not
  // survive an answer change away from NA. Other subs preserve
  // their naReason (their answer isn't changing).
  const targetOverride =
    targetResult === ResponseResult.NA
      ? undefined
      : { naReasonOverrides: { [targetSubQuestionId]: null } };
  const projected = projectSubResponsesForPreservation(existing, targetOverride ?? {});

  const targetIndex = projected.findIndex(
    (entry) => entry.subQuestionId === targetSubQuestionId,
  );
  if (targetIndex >= 0) {
    // Immutable replace via `.map` (not direct assignment) —
    // matches the eslint security/detect-object-injection rule
    // followed elsewhere in the codebase. The target keeps
    // whatever comment / naReason / media / tasks were preserved
    // during projection above; only `result` is overwritten.
    return projected.map((entry, index) =>
      index === targetIndex ? { ...entry, result: targetResult } : entry,
    );
  }
  return [...projected, { subQuestionId: targetSubQuestionId, result: targetResult }];
}

/**
 * Derives the parent's top-level `responseStatus` from the merged
 * children:
 *
 *   - Missing or unanswered child → PENDING
 *   - All children NA → NA
 *   - Every other complete combination → COMPLETED
 */
function deriveTopLevelStatus(
  merged: readonly SubResponse[],
  totalSubQuestions: number,
): ResponseStatus {
  if (totalSubQuestions <= 0 || merged.length < totalSubQuestions) {
    return ResponseStatus.PENDING;
  }
  if (merged.some((entry) => entry.result === undefined)) {
    return ResponseStatus.PENDING;
  }
  return merged.every((entry) => entry.result === ResponseResult.NA)
    ? ResponseStatus.NA
    : ResponseStatus.COMPLETED;
}
